import type { CustomCellEditorProps } from 'ag-grid-react';
import { useGridCellEditor } from 'ag-grid-react';
import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useSyncedRef } from '../../hooks/useSyncedRef';
import { isGridApiReady } from '../BlotterTable/utils';
import { Input } from '../Form/Input';
import { agGridStaticCellClassIsRightAligned } from './agGridStaticCellClass';

export type AgGridInputLiveEditParams = {
  /** Passed to `node.setDataValue` on each change (e.g. for `onCellEditRequest` `source`). */
  source: string;
  /** `source` when the user presses Escape (rollback). */
  escapeRollbackSource: string;
  /** `aria-label` on the input; defaults to `colDef.headerName` when the editor mounts. */
  ariaLabel?: string;
};

function toInputString(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

/**
 * Text cell editor: commits each keystroke with `setDataValue` and the given `source`. Escape
 * rolls back to the value at edit start (including `null`) and uses `escapeRollbackSource`.
 * Enter stops editing after the last live edit.
 */
function AgGridInputLiveEditInner(props: CustomCellEditorProps<unknown, unknown> & AgGridInputLiveEditParams) {
  const inputRef = useRef<HTMLInputElement>(null);
  const propsRef = useSyncedRef(props);
  // Capture initial stored value on edit start: row data is updated by `setDataValue` while the editor is open,
  // so later `props.initialValue` / row data is not the value from when editing started.
  const valueAtEditStartRef = useRef<unknown | null | undefined>(props.initialValue);
  const [local, setLocal] = useState(() => toInputString(props.initialValue));
  const { source, ariaLabel, node, colDef, onKeyDown } = props;

  const forwardKeyDown = useCallback(
    (nativeEvent: KeyboardEvent) => {
      onKeyDown(nativeEvent);
    },
    [onKeyDown]
  );

  useGridCellEditor({
    focusIn() {
      inputRef.current?.focus();
    },
  });

  // Focus and select the input when the editor mounts.
  useLayoutEffect(() => {
    const input = inputRef.current;
    if (input == null) {
      return;
    }
    input.focus();
    input.select();
  }, []);

  const isRightAligned = agGridStaticCellClassIsRightAligned(colDef.cellClass);
  const label = ariaLabel ?? props.colDef?.headerName?.toString() ?? '';

  return (
    <Input
      ref={inputRef}
      aria-label={label}
      value={local}
      type={isRightAligned ? 'number' : 'text'}
      onChange={event => {
        const next = event.target.value;
        setLocal(next);
        props.onValueChange(next);
        if (isGridApiReady(props.api) && props.api.getGridOption('readOnlyEdit') === true) {
          props.node.setDataValue(props.column, next, source);
        }
      }}
      onKeyDownCapture={event => {
        if (event.key === 'Escape') {
          const p = propsRef.current;
          const revert = valueAtEditStartRef.current;
          setLocal(toInputString(revert));
          p.onValueChange(revert);
          if (isGridApiReady(p.api) && p.api.getGridOption('readOnlyEdit') === true) {
            p.node.setDataValue(p.column, revert, p.escapeRollbackSource);
          }
          event.preventDefault();
          event.stopPropagation();
          queueMicrotask(() => {
            if (!isGridApiReady(p.api)) {
              return;
            }
            p.api.stopEditing(true);
          });
          return;
        }
        if (event.key === 'Enter' || event.key === 'NumpadEnter') {
          if (event.nativeEvent.isComposing) {
            forwardKeyDown(event.nativeEvent);
            return;
          }
          const p = propsRef.current;
          event.preventDefault();
          event.stopPropagation();
          queueMicrotask(() => {
            if (!isGridApiReady(p.api)) {
              return;
            }
            p.api.stopEditing(false);
          });
          return;
        }
        forwardKeyDown(event.nativeEvent);
      }}
      style={{
        width: '100%',
        height: `${node.rowHeight}px`,
        background: props.context.current.theme?.backgroundBody,
      }}
    />
  );
}

export const AgGridInputLiveEdit = memo(AgGridInputLiveEditInner);
